import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { createRectangularHolesBatch, getWindowsSize } from '@/utils/buildingWindows';
import { snapToGrid } from '@/utils/geometryModification';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { useWindowSettingsStore } from '@/store/windowSettingsStore';

interface WindowDragPlacementHandlerParams {
	canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
	cameraRef: React.MutableRefObject<THREE.Camera | null>;
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
	enabled: boolean;
}

interface PlacementPoint {
	point: THREE.Vector3;
	normal: THREE.Vector3;
	tangent: THREE.Vector3;
	bitangent: THREE.Vector3;
	width: number;
	height: number;
	localCenterX: number;
	localCenterY: number;
	localNormalOffset: number;
	wallBounds: {
		minX: number;
		maxX: number;
		minY: number;
		maxY: number;
	};
	key: string;
}

interface DragState {
	isDragging: boolean;
	lockedBuildingPath: string | null;
	lockedNormal: THREE.Vector3 | null;
	anchorPlacement: PlacementPoint | null;
	lineDirection: THREE.Vector3 | null;
	placements: PlacementPoint[];
}

const PREVIEW_GROUP_NAME = '__windowDragPreview__';
const NORMAL_ALIGNMENT_THRESHOLD = 0.98;

const getWindowGapFromSnapInterval = (): number => {
	const snapInterval = useWindowSettingsStore.getState().snapPointDistance;
	return Number.isFinite(snapInterval) ? Math.max(0, snapInterval) : 0;
};

const placementKey = (point: THREE.Vector3): string => {
	return `${point.x.toFixed(2)}:${point.y.toFixed(2)}:${point.z.toFixed(2)}`;
};

const disposePreviewGroup = (group: THREE.Group): void => {
	group.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			child.geometry.dispose();
			if (Array.isArray(child.material)) {
				child.material.forEach((mat) => mat.dispose());
			} else {
				child.material.dispose();
			}
			return;
		}

		if (child instanceof THREE.Sprite) {
			child.material.map?.dispose();
			child.material.dispose();
		}
	});
};

const createDistanceLabel = (text: string): THREE.Sprite => {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) {
		throw new Error('Failed to create canvas context for distance label');
	}

	canvas.width = 256;
	canvas.height = 128;
	context.fillStyle = 'rgba(0, 0, 0, 0.72)';
	context.fillRect(0, 0, canvas.width, canvas.height);
	context.font = 'Bold 42px Arial';
	context.fillStyle = '#ffffff';
	context.textAlign = 'center';
	context.textBaseline = 'middle';
	context.fillText(text, canvas.width / 2, canvas.height / 2);

	const texture = new THREE.CanvasTexture(canvas);
	const material = new THREE.SpriteMaterial({
		map: texture,
		depthTest: false,
		depthWrite: false,
	});
	const sprite = new THREE.Sprite(material);
	sprite.scale.set(3.2, 1.4, 1);
	sprite.renderOrder = 1002;
	sprite.userData.ignoreSceneClick = true;
	return sprite;
};

const localToWorldPoint = (
	tangent: THREE.Vector3,
	bitangent: THREE.Vector3,
	normal: THREE.Vector3,
	localX: number,
	localY: number,
	localNormalOffset: number,
	normalOffset: number
): THREE.Vector3 => {
	return new THREE.Vector3()
		.addScaledVector(tangent, localX)
		.addScaledVector(bitangent, localY)
		.addScaledVector(normal, localNormalOffset + normalOffset);
};

const findBuildingMeshByPath = (scene: THREE.Scene, buildingPath: string): THREE.Mesh | null => {
	let found: THREE.Mesh | null = null;
	scene.traverse((child) => {
		if (
			found === null &&
			child instanceof THREE.Mesh &&
			child.userData.category === 'building' &&
			child.userData.path === buildingPath
		) {
			found = child;
		}
	});
	return found;
};

const computePlacementFromIntersection = (
	intersection: THREE.Intersection,
	object: THREE.Mesh
): PlacementPoint | null => {
	if (!intersection.face) {
		return null;
	}

	const faceNormal = intersection.face.normal.clone();
	if (object.matrixWorld) {
		const normalMatrix = new THREE.Matrix3().getNormalMatrix(object.matrixWorld);
		faceNormal.applyMatrix3(normalMatrix).normalize();
	}

	let holeWidth = 2;
	let holeHeight = 2;

	const geom = object.geometry;
	const posAttr = geom.getAttribute('position');
	if (!posAttr) {
		return null;
	}

	const idx = intersection.face.a;
	const idy = intersection.face.b;
	const idz = intersection.face.c;
	const va = new THREE.Vector3().fromBufferAttribute(posAttr, idx);
	const vb = new THREE.Vector3().fromBufferAttribute(posAttr, idy);
	const vc = new THREE.Vector3().fromBufferAttribute(posAttr, idz);

	const zUp = new THREE.Vector3(0, 0, 1);
	const tangent = new THREE.Vector3().crossVectors(zUp, faceNormal);
	if (tangent.lengthSq() < 1e-6) {
		tangent.set(1, 0, 0);
	}
	tangent.normalize();

	const bitangent = new THREE.Vector3().crossVectors(faceNormal, tangent).normalize();

	let minLocalX = Infinity;
	let maxLocalX = -Infinity;
	let minLocalY = Infinity;
	let maxLocalY = -Infinity;
	for (const vertex of [va, vb, vc]) {
		const lx = vertex.dot(tangent);
		const ly = vertex.dot(bitangent);
		minLocalX = Math.min(minLocalX, lx);
		maxLocalX = Math.max(maxLocalX, lx);
		minLocalY = Math.min(minLocalY, ly);
		maxLocalY = Math.max(maxLocalY, ly);
	}

	const wallWidth = Math.max(0, maxLocalX - minLocalX);
	const wallHeight = Math.max(0, maxLocalY - minLocalY);
	const wallArea = wallWidth * wallHeight;
	const { width, height } = getWindowsSize(wallHeight, wallArea, wallWidth);
	holeWidth = width;
	holeHeight = height;

	const point = intersection.point.clone();
	const minZ = Math.min(va.z, vb.z, vc.z);
	const maxZ = Math.max(va.z, vb.z, vc.z);
	const halfHeight = wallHeight / 2;
	if (point.z - halfHeight < minZ) point.z = minZ + halfHeight;
	if (point.z + halfHeight > maxZ) point.z = maxZ - halfHeight;

	const snappedPoint = snapToGrid(
		point,
		object,
		intersection.face,
		tangent,
		bitangent,
		faceNormal,
		holeWidth,
		holeHeight
	);

	return {
		point: snappedPoint,
		normal: faceNormal,
		tangent,
		bitangent,
		width: holeWidth,
		height: holeHeight,
		localCenterX: snappedPoint.dot(tangent),
		localCenterY: snappedPoint.dot(bitangent),
		localNormalOffset: snappedPoint.dot(faceNormal),
		wallBounds: {
			minX: minLocalX,
			maxX: maxLocalX,
			minY: minLocalY,
			maxY: maxLocalY,
		},
		key: placementKey(snappedPoint),
	};
};

const buildHorizontalPlacements = (
	anchor: PlacementPoint,
	lineDirection: THREE.Vector3,
	targetPoint: THREE.Vector3
): PlacementPoint[] => {
	const windowGap = getWindowGapFromSnapInterval();
	const rawStride = anchor.width + windowGap;
	const stride = Number.isFinite(rawStride) && rawStride > 0.001 ? rawStride : 0.001;
	const offset = targetPoint.clone().sub(anchor.point);
	const signedDistance = offset.dot(lineDirection);
	const directionSign = signedDistance < 0 ? -1 : 1;
	const steps = Math.floor(Math.abs(signedDistance) / stride);

	const placements: PlacementPoint[] = [anchor];
	for (let i = 1; i <= steps; i++) {
		const nextPoint = anchor.point
			.clone()
			.addScaledVector(lineDirection, directionSign * i * stride);
		placements.push({
			point: nextPoint,
			normal: anchor.normal,
			tangent: anchor.tangent,
			bitangent: anchor.bitangent,
			width: anchor.width,
			height: anchor.height,
			localCenterX: anchor.localCenterX + directionSign * i * stride,
			localCenterY: anchor.localCenterY,
			localNormalOffset: anchor.localNormalOffset,
			wallBounds: anchor.wallBounds,
			key: placementKey(nextPoint),
		});
	}

	return placements;
};

const updatePreviewGroup = (
	scene: THREE.Scene,
	previewGroupRef: React.MutableRefObject<THREE.Group | null>,
	placements: PlacementPoint[]
): void => {
	if (previewGroupRef.current) {
		scene.remove(previewGroupRef.current);
		disposePreviewGroup(previewGroupRef.current);
		previewGroupRef.current = null;
	}

	if (placements.length === 0) {
		return;
	}

	const group = new THREE.Group();
	group.name = PREVIEW_GROUP_NAME;
	group.userData.ignoreSceneClick = true;
	group.userData.isWindowDragPreview = true;

	for (const placement of placements) {
		const plane = new THREE.Mesh(
			new THREE.PlaneGeometry(placement.width, placement.height),
			new THREE.MeshBasicMaterial({
				color: 0x00a2ff,
				transparent: true,
				opacity: 0.28,
				side: THREE.DoubleSide,
				depthTest: false,
			})
		);
		const matrix = new THREE.Matrix4().makeBasis(
			placement.tangent,
			placement.bitangent,
			placement.normal
		);
		plane.rotation.setFromRotationMatrix(matrix);
		plane.position.copy(placement.point).addScaledVector(placement.normal, 0.05);
		plane.renderOrder = 1001;
		group.add(plane);
	}

	const firstPlacement = placements[0];
	const rowMinX = Math.min(...placements.map((placement) => placement.localCenterX - placement.width / 2));
	const rowMaxX = Math.max(...placements.map((placement) => placement.localCenterX + placement.width / 2));
	const rowMinY = Math.min(...placements.map((placement) => placement.localCenterY - placement.height / 2));
	const rowMaxY = Math.max(...placements.map((placement) => placement.localCenterY + placement.height / 2));
	const rowCenterX = (rowMinX + rowMaxX) / 2;
	const rowCenterY = (rowMinY + rowMaxY) / 2;
	const { minX, maxX, minY, maxY } = firstPlacement.wallBounds;
	const guideOffset = 0.22;

	const guides = [
		{
			distance: rowMinX - minX,
			position: localToWorldPoint(
				firstPlacement.tangent,
				firstPlacement.bitangent,
				firstPlacement.normal,
				(minX + rowMinX) / 2,
				rowCenterY,
				firstPlacement.localNormalOffset,
				guideOffset
			),
		},
		{
			distance: maxX - rowMaxX,
			position: localToWorldPoint(
				firstPlacement.tangent,
				firstPlacement.bitangent,
				firstPlacement.normal,
				(rowMaxX + maxX) / 2,
				rowCenterY,
				firstPlacement.localNormalOffset,
				guideOffset
			),
		},
		{
			distance: rowMinY - minY,
			position: localToWorldPoint(
				firstPlacement.tangent,
				firstPlacement.bitangent,
				firstPlacement.normal,
				rowCenterX,
				(minY + rowMinY) / 2,
				firstPlacement.localNormalOffset,
				guideOffset
			),
		},
		{
			distance: maxY - rowMaxY,
			position: localToWorldPoint(
				firstPlacement.tangent,
				firstPlacement.bitangent,
				firstPlacement.normal,
				rowCenterX,
				(rowMaxY + maxY) / 2,
				firstPlacement.localNormalOffset,
				guideOffset
			),
		},
	];

	for (const guide of guides) {
		if (guide.distance <= 0.01) {
			continue;
		}

		const label = createDistanceLabel(`${guide.distance.toFixed(2)}m`);
		label.position.copy(guide.position);
		group.add(label);
	}

	scene.add(group);
	previewGroupRef.current = group;
};

export const useWindowDragPlacementHandler = (params: WindowDragPlacementHandlerParams) => {
	const { canvasRef, cameraRef, sceneRef, enabled } = params;
	const dragStateRef = useRef<DragState>({
		isDragging: false,
		lockedBuildingPath: null,
		lockedNormal: null,
		anchorPlacement: null,
		lineDirection: null,
		placements: [],
	});
	const previewGroupRef = useRef<THREE.Group | null>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const camera = cameraRef.current;
		const scene = sceneRef.current;

		if (!canvas || !camera || !scene) {
			return;
		}

		const raycaster = new THREE.Raycaster();
		const mouse = new THREE.Vector2();

		const getBuildingIntersection = (event: MouseEvent): { hit: THREE.Intersection; mesh: THREE.Mesh } | null => {
			const rect = canvas.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
			raycaster.setFromCamera(mouse, camera);

			const buildingObjects: THREE.Object3D[] = [];
			scene.traverse((child) => {
				if (child instanceof THREE.Mesh && child.userData.category === 'building') {
					buildingObjects.push(child);
				}
			});

			const intersects = raycaster.intersectObjects(buildingObjects, true);
			const firstHit = intersects.find((hit) => hit.object instanceof THREE.Mesh && !!hit.face);
			if (!firstHit || !(firstHit.object instanceof THREE.Mesh)) {
				return null;
			}

			return { hit: firstHit, mesh: firstHit.object };
		};

		const resetDragState = () => {
			dragStateRef.current = {
				isDragging: false,
				lockedBuildingPath: null,
				lockedNormal: null,
				anchorPlacement: null,
				lineDirection: null,
				placements: [],
			};
		};

		const clearPreview = () => {
			updatePreviewGroup(scene, previewGroupRef, []);
		};

		const cancelDrag = () => {
			if (!dragStateRef.current.isDragging) {
				clearPreview();
				return;
			}

			resetDragState();
			clearPreview();
			useThreeSceneStore.getState().setAreEventsPaused(false);
		};

		const handleMouseDown = (event: MouseEvent) => {
			if (!enabled || event.button !== 0) return;

			const intersection = getBuildingIntersection(event);
			if (!intersection) return;

			event.preventDefault();
			event.stopPropagation();

			const placement = computePlacementFromIntersection(intersection.hit, intersection.mesh);
			if (!placement) {
				return;
			}

			const zUp = new THREE.Vector3(0, 0, 1);
			const lineDirection = new THREE.Vector3().crossVectors(zUp, placement.normal);
			if (lineDirection.lengthSq() < 1e-6) {
				lineDirection.set(1, 0, 0);
			}
			lineDirection.normalize();

			dragStateRef.current = {
				isDragging: true,
				lockedBuildingPath: intersection.mesh.userData.path ?? intersection.mesh.uuid,
				lockedNormal: placement.normal.clone(),
				anchorPlacement: placement,
				lineDirection,
				placements: [placement],
			};

			updatePreviewGroup(scene, previewGroupRef, dragStateRef.current.placements);
		};

		const handleMouseMove = (event: MouseEvent) => {
			if (!enabled) {
				clearPreview();
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const intersection = getBuildingIntersection(event);
			if (!intersection) {
				if (!dragStateRef.current.isDragging) {
					clearPreview();
				}
				return;
			}

			const placement = computePlacementFromIntersection(intersection.hit, intersection.mesh);
			if (!placement) {
				if (!dragStateRef.current.isDragging) {
					clearPreview();
				}
				return;
			}

			if (!dragStateRef.current.isDragging) {
				updatePreviewGroup(scene, previewGroupRef, [placement]);
				return;
			}

			const currentPath = intersection.mesh.userData.path ?? intersection.mesh.uuid;
			if (currentPath !== dragStateRef.current.lockedBuildingPath) {
				return;
			}

			if (
				!dragStateRef.current.lockedNormal ||
				!dragStateRef.current.anchorPlacement ||
				!dragStateRef.current.lineDirection
			) {
				return;
			}

			if (placement.normal.dot(dragStateRef.current.lockedNormal) < NORMAL_ALIGNMENT_THRESHOLD) {
				return;
			}

			dragStateRef.current.placements = buildHorizontalPlacements(
				dragStateRef.current.anchorPlacement,
				dragStateRef.current.lineDirection,
				placement.point
			);

			updatePreviewGroup(scene, previewGroupRef, dragStateRef.current.placements);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}

			event.preventDefault();
			cancelDrag();
		};

		const handleContextMenu = (event: MouseEvent) => {
			if (!enabled) {
				return;
			}

			if (dragStateRef.current.isDragging) {
				event.preventDefault();
				event.stopPropagation();
				cancelDrag();
			}
		};

		const handleMouseUp = async (event: MouseEvent) => {
			if (!enabled || !dragStateRef.current.isDragging || event.button !== 0) return;

			event.preventDefault();
			event.stopPropagation();

			const placements = [...dragStateRef.current.placements];
			const buildingPath = dragStateRef.current.lockedBuildingPath;

			resetDragState();
			updatePreviewGroup(scene, previewGroupRef, []);

			if (!buildingPath || placements.length === 0) {
				useThreeSceneStore.getState().setAreEventsPaused(false);
				return;
			}

			try {
				useThreeSceneStore.getState().setAreEventsPaused(true);
				const buildingMesh = findBuildingMeshByPath(scene, buildingPath);
				if (!buildingMesh) {
					return;
				}

				await createRectangularHolesBatch(
					buildingMesh,
					placements.map((placement) => ({
						position: placement.point,
						normal: placement.normal,
						width: placement.width,
						height: placement.height,
					})),
					scene
				);
			} catch (error) {
				console.error('Error while creating dragged windows:', error);
			} finally {
				useThreeSceneStore.getState().setAreEventsPaused(false);
			}
		};

		const handleMouseLeave = () => {
			cancelDrag();
		};

		canvas.addEventListener('mousedown', handleMouseDown, true);
		canvas.addEventListener('mousemove', handleMouseMove, true);
		canvas.addEventListener('mouseup', handleMouseUp, true);
		canvas.addEventListener('mouseleave', handleMouseLeave, true);
		canvas.addEventListener('contextmenu', handleContextMenu, true);
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			canvas.removeEventListener('mousedown', handleMouseDown, true);
			canvas.removeEventListener('mousemove', handleMouseMove, true);
			canvas.removeEventListener('mouseup', handleMouseUp, true);
			canvas.removeEventListener('mouseleave', handleMouseLeave, true);
			canvas.removeEventListener('contextmenu', handleContextMenu, true);
			window.removeEventListener('keydown', handleKeyDown);
			clearPreview();
			useThreeSceneStore.getState().setAreEventsPaused(false);
		};
	}, [canvasRef, cameraRef, sceneRef, enabled]);
};
